import { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { yupResolver } from '@hookform/resolvers/yup';

import { FormProvider, useForm } from '@hooks/react-hook-form';
import { Button, useSnackbar } from '@new-components/elements';
import {
  FormParam,
  SavedApplicationDataResponse,
  useRGetApplicationData,
  useRGetSavedApplication,
  useRSaveApplication,
} from '@repositories/new-application';
import { excludeKey, setErrors } from '@utils/object';

import ApplicationCard from '../../application-card';

import { FamilyInformationProps } from './types';
import {
  SectionBiologicalFamily,
  SectionCloseFamily,
} from './components/sections';
import { defaultValues, familyInformationSchema } from './constants';
import { useActionLog } from '@hooks/use-action-log';

const FamilyInformation = (props: FamilyInformationProps) => {
  const { setStep } = props;
  const { onOpen } = useSnackbar();

  useActionLog({ page: 'app_form_family_information_page' });

  const form = useForm<FormParam<'family_information'>>({
    defaultValues,
    shouldFocusError: false,
    resolver: yupResolver(familyInformationSchema),
    mode: 'onChange',
  });

  const { handleSubmit, setError, reset } = form;

  const { isLoading: isLoadingGetApplicationData } = useRGetApplicationData({
    page: 2,
  });

  const { mutateAsync: mutateAsyncSaveForm, isLoading: isSavingForm } =
    useRSaveApplication({
      onError: (err) => {
        const statusCode = err.payload?.statusCode;
        const errors = (err.payload?.errors ||
          {}) as FormParam<'family_information'>;

        switch (statusCode) {
          case 400:
            setErrors<FormParam<'family_information'>>(errors, setError);
            break;

          default:
            onOpen({
              message:
                (err.payload?.errors as Array<string>)?.[0] ||
                'Opps, Something went wrong.',
              severity: 'error',
            });
            break;
        }
      },
    });

  async function onSubmit(fields: FormParam<'family_information'>) {
    try {
      await mutateAsyncSaveForm(
        {
          variables: {
            ...excludeKey<SavedApplicationDataResponse<'family_information'>>(
              fields as SavedApplicationDataResponse<'family_information'>,
              ['currentStep'],
            ),
            step: 2,
          },
        },
        {
          onSuccess: () => {
            setStep((prev) => prev + 1);
          },
        },
      );
    } catch (error) {
      return;
    }
  }

  useRGetSavedApplication<SavedApplicationDataResponse<'family_information'>>({
    step: 2,
    onSuccess: ({ data }) => {
      reset(data);
    },
  });

  return (
    <FormProvider scrollToFieldOnError {...form}>
      <Helmet>
        <title>JULO - Form Informasi Keluarga</title>
      </Helmet>

      <form onSubmit={handleSubmit(onSubmit)} className='family-information'>
        <SectionCloseFamily />
        <SectionBiologicalFamily />
        <ApplicationCard>
          <Button
            type='submit'
            disabled={isSavingForm || isLoadingGetApplicationData}
            fullWidth
          >
            Lanjutkan
          </Button>
        </ApplicationCard>
      </form>
    </FormProvider>
  );
};

export default memo(FamilyInformation);
